import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const MyLibrary = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) navigate('/newsletter');
      else fetchMyItems(session.user.id);
    });
  }, [navigate]);

  const fetchMyItems = async (userId) => {
    setLoading(true);
    try {
      // 1. Fetch Paid Orders
      const { data: orders } = await supabase
        .from('product_orders_1779580000000')
        .select('*, product:digital_products_1779580000000(*)')
        .eq('user_id', userId)
        .eq('status', 'completed');

      // 2. Fetch Free Downloads
      const { data: freebies } = await supabase
        .from('guide_downloads_1779668873179')
        .select('*')
        .eq('user_id', userId);

      // 3. Fetch Product data for freebies (to get the URL)
      const { data: guideDetails } = await supabase
        .from('digital_products_1779580000000')
        .select('*')
        .eq('slug', 'responsible-leaders-guide')
        .single();

      // Combine them for the UI
      const libraryItems = [
        ...(orders?.map(o => ({ 
          title: o.product.title, 
          download_url: o.product.download_url, 
          type: 'Paid', 
          date: o.created_at 
        })) || []),
        ...(freebies?.map(f => ({ 
          title: "Responsible Leader's Guide", 
          download_url: guideDetails?.download_url || 'https://canva.link/1sqjdywz7gpmtn7',
          type: 'Free',
          date: f.created_at 
        })) || [])
      ].sort((a, b) => new Date(b.date) - new Date(a.date));

      setItems(libraryItems);
    } catch (err) {
      console.error('Error loading library:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <MainLayout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <SafeIcon icon={FiIcons.FiLoader} className="w-10 h-10 text-brand-teal animate-spin" />
      </div>
    </MainLayout>
  );

  return (
    <MainLayout>
      <Section bg="cream" className="pt-20">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h1 className="text-4xl font-bold text-brand-navy mb-2">My Library</h1>
              <p className="text-gray-600">Secure access to your mission-led AI resources.</p>
            </div>
            <Button onClick={() => supabase.auth.signOut().then(() => navigate('/'))} variant="ghost" className="text-red-500">
              Sign Out
            </Button>
          </div>

          {items.length === 0 ? (
            <div className="bg-white p-12 rounded-3xl text-center border border-dashed border-gray-300">
              <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiIcons.FiBook} className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-navy mb-2">Your library is empty</h3>
              <p className="text-gray-500 mb-8">Sign up for the Leader's Guide to see it here.</p>
              <Button to="/resources/guide" variant="primary">Get the Guide</Button>
            </div>
          ) : (
            <div className="grid gap-6">
              {items.map((item, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="flex items-center gap-6 w-full">
                    <div className="w-16 h-16 bg-brand-teal/10 rounded-xl flex items-center justify-center text-brand-teal shrink-0">
                      <SafeIcon icon={item.type === 'Paid' ? FiIcons.FiShoppingBag : FiIcons.FiGift} className="w-8 h-8" />
                    </div>
                    <div>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${item.type === 'Paid' ? 'bg-brand-gold/20 text-brand-navy' : 'bg-brand-teal/10 text-brand-teal'}`}>
                        {item.type}
                      </span>
                      <h3 className="text-xl font-bold text-brand-navy mt-1">{item.title}</h3>
                      <p className="text-sm text-gray-500">Added on {new Date(item.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Button href={item.download_url} variant="outline" className="whitespace-nowrap w-full md:w-auto">
                    <SafeIcon icon={FiIcons.FiExternalLink} className="mr-2" />
                    Access Resource
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>
    </MainLayout>
  );
};

export default MyLibrary;